/**
 * Auth error mapping for the animateur manager-access login flow.
 *
 * The auth service returns RFC 7807 Problem Details (see the auth service
 * ERROR_CODES catalog, `animateur/*` section): a stable machine-readable `type`,
 * plus `title`, `status` and a human-readable `detail`. The login screen
 * previously surfaced the raw axios message ("Request failed with status code
 * 401") for the request step and swallowed the polling/tester errors entirely.
 *
 * This module extracts the problem body from an axios error/response and maps
 * the `type` to a specific, localized message key under
 * `App.LoginRequestBox.errors.*`. Switch on `type` only — never on
 * `title`/`detail`, which may change across versions.
 */

export type AuthProblem = {
  type?: string;
  detail?: string;
  status?: number;
};

// react-i18next's `t` (TFunction) has an overloaded signature that does not
// structurally match a plain function type, so it is accepted loosely (in line
// with this codebase's style). Each call is `t(key, defaultValue)`.
type Translate = any;

/**
 * Extract an {@link AuthProblem} from an axios error, an axios response, or a
 * raw response-data object. Returns null when there is no HTTP response at all
 * (network failure / timeout), which the caller maps to a connectivity message.
 */
export const extractProblem = (input: any): AuthProblem | null => {
  if (!input) return null;

  // axios error → has `response`; plain response → has `data`+`status`
  const response = input.response ?? (input.data !== undefined ? input : null);
  if (!response) return null; // no HTTP response → network error

  const data = response.data;
  const status = response.status;

  if (data && typeof data === 'object') {
    return {
      type: typeof data.type === 'string' ? data.type : undefined,
      detail: typeof data.detail === 'string' ? data.detail : undefined,
      status: typeof data.status === 'number' ? data.status : status,
    };
  }
  return { status };
};

const KEY = (k: string) => `App.LoginRequestBox.errors.${k}`;

/** RFC 7807 `type` → `App.LoginRequestBox.errors.*` key. */
const TYPE_TO_KEY: Record<string, string> = {
  // animateur/* — manager-access flow
  'animateur/email-not-found': KEY('emailNotFound'),
  'animateur/code-mismatch': KEY('codeMismatch'),
  'animateur/pending-approval': KEY('pendingApproval'),
  'animateur/session-failed': KEY('sessionFailed'),
  'animateur/internal-error': KEY('internalError'),

  // auth/* — reachable via the App Store tester login (CoreAPI /login)
  'auth/invalid-credentials': KEY('invalidCredentials'),
  'auth/company-inactive': KEY('companyInactive'),
  'auth/service-unavailable': KEY('serviceUnavailable'),
  'auth/invalid-contact': KEY('invalidEmail'),
};

/** English fallbacks so a message always shows even before a locale is synced. */
const DEFAULTS: Record<string, string> = {
  [KEY('invalidEmail')]: 'Please enter a valid email address.',
  [KEY('emailNotFound')]:
    'No active store manager account was found for that email. Please check the address.',
  [KEY('codeMismatch')]:
    'The access code is incorrect. Please check the code and try again.',
  [KEY('pendingApproval')]: 'Waiting for manager approval…',
  [KEY('sessionFailed')]:
    'Unable to start your session. Please ask the manager to approve again.',
  [KEY('internalError')]: 'An unexpected error occurred. Please try again.',
  [KEY('invalidCredentials')]:
    'The email or password you entered is incorrect.',
  [KEY('companyInactive')]:
    "Your organization's account is not active. Please contact your administrator.",
  [KEY('serviceUnavailable')]:
    'Our service is temporarily unavailable. Please try again in a moment.',
  [KEY('network')]:
    "We couldn't reach the server. Check your connection and try again.",
  [KEY('unexpected')]: 'An unexpected error occurred. Please try again.',
};

const translate = (t: Translate, key: string): string =>
  t(key, DEFAULTS[key] ?? DEFAULTS[KEY('unexpected')]);

/**
 * Resolve a specific, localized error message from a captured problem.
 *
 * Resolution order: known `type` → HTTP status heuristic → server `detail` →
 * generic fallback. Pass `problem = null` (no HTTP response) to get the
 * connectivity message.
 */
export const getAnimateurErrorMessage = (
  t: Translate,
  problem: AuthProblem | null | undefined,
): string => {
  if (problem === null) return translate(t, KEY('network'));
  if (!problem) return translate(t, KEY('unexpected'));

  if (problem.type && TYPE_TO_KEY[problem.type]) {
    return translate(t, TYPE_TO_KEY[problem.type]);
  }

  const status = problem.status;
  if (typeof status === 'number' && status >= 500) {
    return translate(t, KEY('serviceUnavailable'));
  }

  const detail = problem.detail?.trim();
  if (detail) return detail;

  return translate(t, KEY('unexpected'));
};

/** True for aborted requests (poll cancellation), which must not be shown. */
export const isAbortError = (error: any): boolean =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED';
